'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const parseDateValue = (value?: string | number) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const next = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(next.getTime()) ? null : next;
};

const formatDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateDisplay = (value?: string | number) => {
  const parsed = parseDateValue(value);
  if (!parsed) return '';
  return `${`${parsed.getDate()}`.padStart(2, '0')}:${`${parsed.getMonth() + 1}`.padStart(2, '0')}:${parsed.getFullYear()}`;
};

const parseTimeValue = (value?: string | number) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
};

const formatTimeValue = (hours24: number, minutes: number) =>
  `${`${hours24}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;

const formatTimeDisplay = (value?: string | number) => {
  const parsed = parseTimeValue(value);
  if (!parsed) return '';
  return `${`${parsed.hours}`.padStart(2, '0')}:${`${parsed.minutes}`.padStart(2, '0')}`;
};

const buildCalendarCells = (viewMonth: Date) => {
  const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const cell = new Date(gridStart);
    cell.setDate(gridStart.getDate() + index);
    return cell;
  });
};

const DatePicker: React.FC<{
  value: string | number;
  placeholder?: string;
  onChange: (v: string) => void;
}> = ({ value, placeholder, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseDateValue(value);
  const [viewMonth, setViewMonth] = useState<Date>(selectedDate || new Date());

  useEffect(() => {
    const nextSelected = parseDateValue(value);
    if (nextSelected) setViewMonth(nextSelected);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const cells = useMemo(() => buildCalendarCells(viewMonth), [viewMonth]);
  const displayValue = formatDateDisplay(value);
  const today = new Date();
  const todayKey = formatDateValue(today);
  const selectedKey = selectedDate ? formatDateValue(selectedDate) : '';

  return (
    <div ref={containerRef} className={`relative space-y-1.5 ${open ? 'z-[160]' : 'z-0'}`}>
      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.16em]">Date</label>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="group relative w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-sky-50/30 px-4 py-3 text-left shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-all hover:border-sky-300/70 hover:shadow-[0_12px_28px_rgba(14,165,233,0.08)]"
      >
        <div className="pr-12 text-[1.05rem] leading-none font-extrabold tracking-[-0.01em] text-slate-800">
          {displayValue || placeholder || 'Select date'}
        </div>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-sky-200/70 bg-white/90 shadow-[0_4px_12px_rgba(14,165,233,0.08)]">
            <CalendarDays className="h-3.5 w-3.5 text-sky-700" />
          </div>
        </div>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[180] mt-2 w-[260px] rounded-[1.6rem] border border-slate-200/90 bg-white p-3 shadow-[0_18px_40px_rgba(15,23,42,0.16)]">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-center">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-sky-600">Mission Date</div>
              <div className="mt-1 text-base font-black text-slate-900">
                {MONTH_LABELS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="text-center text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell) => {
              const cellKey = formatDateValue(cell);
              const isSelected = cellKey === selectedKey;
              const isCurrentMonth = cell.getMonth() === viewMonth.getMonth();
              const isToday = cellKey === todayKey;
              return (
                <button
                  key={cellKey}
                  type="button"
                  onClick={() => {
                    onChange(cellKey);
                    setOpen(false);
                  }}
                  className={`flex h-7 items-center justify-center rounded-lg text-[13px] font-bold transition ${
                    isSelected
                      ? 'bg-gradient-to-r from-sky-600 to-cyan-500 text-white shadow-[0_10px_24px_rgba(14,165,233,0.28)]'
                      : isToday
                        ? 'border border-sky-300 bg-sky-50 text-sky-700'
                        : isCurrentMonth
                          ? 'text-slate-800 hover:bg-slate-100'
                          : 'text-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TimePicker: React.FC<{
  label: string;
  value: string | number;
  placeholder?: string;
  onChange: (v: string) => void;
}> = ({ label, value, placeholder, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const parsedValue = parseTimeValue(value) || { hours: 8, minutes: 0 };
  const [draftHour, setDraftHour] = useState<number>(parsedValue.hours);
  const [draftMinute, setDraftMinute] = useState<number>(parsedValue.minutes);

  useEffect(() => {
    const next = parseTimeValue(value);
    if (!next) return;
    setDraftHour(next.hours);
    setDraftMinute(next.minutes);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const displayValue = formatTimeDisplay(value);
  const hourOptions = Array.from({ length: 24 }, (_, index) => index);
  const minuteOptions = Array.from({ length: 60 }, (_, index) => index);
  const commit = (hour24: number, minute: number) => {
    onChange(formatTimeValue(hour24, minute));
  };

  return (
    <div ref={containerRef} className={`relative space-y-1.5 ${open ? 'z-[160]' : 'z-0'}`}>
      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.16em]">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="group relative w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-sky-50/30 px-4 py-3 text-left shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-all hover:border-sky-300/70 hover:shadow-[0_12px_28px_rgba(14,165,233,0.08)]"
      >
        <div className="pr-12 text-[1.05rem] leading-none font-extrabold tracking-[-0.01em] text-slate-800">
          {displayValue || placeholder || 'Select time'}
        </div>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-sky-200/70 bg-white/90 shadow-[0_4px_12px_rgba(14,165,233,0.08)]">
            <Clock3 className="h-3.5 w-3.5 text-sky-700" />
          </div>
        </div>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[180] mt-2 w-[216px] rounded-[1.6rem] border border-slate-200/90 bg-white p-3 shadow-[0_18px_40px_rgba(15,23,42,0.16)]">
          <div className="mb-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-sky-600">{label}</div>
            <div className="mt-1 text-base font-black text-slate-900">Select time</div>
          </div>
          <div className="grid grid-cols-[92px_92px] gap-2">
            <div>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Hour</div>
              <div className="max-h-44 space-y-1 overflow-y-auto pr-1 custom-scrollbar">
                {hourOptions.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => {
                      setDraftHour(hour);
                      commit(hour, draftMinute);
                    }}
                    className={`flex h-8 w-full items-center justify-center rounded-lg text-[13px] font-black transition ${
                      draftHour === hour
                        ? 'bg-gradient-to-r from-sky-600 to-cyan-500 text-white shadow-[0_10px_24px_rgba(14,165,233,0.28)]'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {`${hour}`.padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Minute</div>
              <div className="max-h-44 space-y-1 overflow-y-auto pr-1 custom-scrollbar">
                {minuteOptions.map((minute) => (
                  <button
                    key={minute}
                    type="button"
                    onClick={() => {
                      setDraftMinute(minute);
                      commit(draftHour, minute);
                    }}
                    className={`flex h-8 w-full items-center justify-center rounded-lg text-[13px] font-black transition ${
                      draftMinute === minute
                        ? 'bg-gradient-to-r from-sky-600 to-cyan-500 text-white shadow-[0_10px_24px_rgba(14,165,233,0.28)]'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {`${minute}`.padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const LegInput: React.FC<{ 
  label: string; 
  value: string | number; 
  onChange: (v: string) => void; 
  type?: string;
  placeholder?: string;
}> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}) => {
  if (type === 'date') {
    return <DatePicker value={value} placeholder={placeholder} onChange={onChange} />;
  }

  if (type === 'time') {
    return <TimePicker label={label} value={value} placeholder={placeholder} onChange={onChange} />;
  }

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.16em]">{label}</label>
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-sky-50/20 shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-all hover:border-sky-300/60 hover:shadow-[0_12px_28px_rgba(14,165,233,0.08)]">
        <input
          type={type}
          placeholder={placeholder}
          className="w-full rounded-2xl bg-transparent px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition-all focus:ring-2 focus:ring-sky-500/15"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </div>
    </div>
  );
};

export const AirstripInput: React.FC<{
  label: string;
  value: string;
  options: { code: string; name: string; icao?: string }[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);

  const formatOptionLabel = (opt?: { code: string; name: string; icao?: string }) =>
    opt ? `${opt.name} (${opt.icao || opt.code})` : '';

  const findByCode = (rawCode: string) =>
    options.find(opt => opt.code.toUpperCase() === String(rawCode || '').trim().toUpperCase());

  useEffect(() => {
    const selected = findByCode(value);
    setQuery(selected ? formatOptionLabel(selected) : (value || ''));
  }, [value]);

  const normalized = query.trim().toUpperCase();
  const resolveToCode = (raw: string) => {
    const token = raw.trim().toUpperCase();
    if (!token) return '';
    const match = options.find(opt =>
      opt.code.toUpperCase() === token ||
      (opt.icao || '').toUpperCase() === token ||
      opt.name.toUpperCase() === token ||
      `${opt.name.toUpperCase()} (${((opt.icao || opt.code).toUpperCase())})` === token
    );
    return match ? match.code.toUpperCase() : token;
  };
  const filtered = options.filter(opt => {
    if (!normalized) return true;
    const icao = (opt.icao || '').toUpperCase();
    return (
      opt.code.toUpperCase().includes(normalized) ||
      icao.includes(normalized) ||
      opt.name.toUpperCase().includes(normalized)
    );
  });

  const commitSelection = (code: string) => {
    const next = code.toUpperCase();
    const selected = findByCode(next);
    setQuery(next);
    if (selected) {
      setQuery(formatOptionLabel(selected));
    }
    setOpen(false);
    onChange(next);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setOpen(false);
      if (!normalized) {
        setQuery(value || '');
        return;
      }
      const resolvedCode = resolveToCode(normalized);
      const exact = options.find(opt => opt.code.toUpperCase() === resolvedCode);
      if (!exact) {
        setQuery(value || '');
        return;
      }
      setQuery(formatOptionLabel(exact));
      onChange(exact.code.toUpperCase());
    }, 120);
  };

  return (
    <div className="space-y-1.5 relative">
      <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.16em]">{label}</label>
      <input
        className="w-full rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-sky-50/20 px-4 py-3 text-sm font-semibold text-slate-800 outline-none shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-all focus:ring-2 focus:ring-sky-500/15 focus:border-sky-400 hover:border-sky-300/60 hover:shadow-[0_12px_28px_rgba(14,165,233,0.08)]"
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          const selected = findByCode(value);
          if (selected) setQuery(selected.code.toUpperCase());
          setOpen(true);
        }}
        onBlur={handleBlur}
        placeholder="ICAO/IATA"
      />
      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 max-h-56 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-[0_18px_34px_rgba(15,23,42,0.12)] z-20">
          {filtered.map(opt => (
            <button
              key={opt.code}
              type="button"
              onMouseDown={() => commitSelection(opt.code)}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-all hover:bg-sky-50/70"
            >
              <span className="text-sm font-semibold text-slate-900 truncate">
                {opt.name} ({opt.icao || opt.code})
              </span>
              <span className="text-xs text-slate-500">{opt.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AncillaryToggle: React.FC<{ label: string; active?: boolean; onToggle: () => void }> = ({
  label,
  active,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all border ${
      active
        ? 'bg-indigo-600 text-white border-indigo-600'
        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
    }`}
  >
    {label}
  </button>
);

export const CostLine: React.FC<{ label: string; value: number; highlight?: boolean }> = ({
  label,
  value,
  highlight = false,
}) => (
  <div className="flex justify-between items-center py-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm font-mono font-semibold ${highlight ? 'text-indigo-600' : 'text-slate-900'}`}>
      ${Math.round(value).toLocaleString()}
    </span>
  </div>
);
